import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { MagnifyingGlass, CheckCircle, CaretDown, CaretUp, PaperPlaneTilt, Plus, X } from "@phosphor-icons/react";
import api, { formatApiError } from "../api";
import { BrutalButton, BrutalInput } from "./Brutal";
import { buildProductIndex, matchProducts } from "../lib/productMatch";

// v409.7 — full supplier catalog inside the distributor dashboard.
// Role-aware pricing comes from the backend (subs see margin-adjusted WS,
// pricing-pending subs see no WS); availability is a boolean by design.
export const SupplierCatalog = ({ pendingRequests = [], onRequested }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [catalog, setCatalog] = useState(null);
  const [q, setQ] = useState("");
  const [cart, setCart] = useState({}); // v409.8 — {productId: {p, qty}}
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || catalog !== null) return;
    api.get("/restock-requests/catalog")
      .then(({ data }) => setCatalog(data || []))
      .catch((e) => { toast.error(formatApiError(e)); setCatalog([]); });
  }, [open, catalog]);

  const pendingIds = useMemo(
    () => new Set(pendingRequests.map((r) => r.product_id)),
    [pendingRequests]
  );

  const index = useMemo(() => buildProductIndex((catalog || []).map((p) => p.name)), [catalog]);
  const list = useMemo(() => {
    const needle = q.trim();
    if (!needle) return catalog || [];
    // Smart shorthand search ("reta 10", "bb20") + plain substring fallback.
    const hits = new Set(matchProducts(needle, index, 50));
    return (catalog || []).filter(
      (p) => hits.has(p.name) || p.name.toLowerCase().includes(needle.toLowerCase())
    );
  }, [catalog, q, index]);

  const cartItems = Object.values(cart);
  const setQtyFor = (p, qty) => setCart((c) => ({ ...c, [p.id]: { p, qty } }));
  const removeFromCart = (id) => setCart((c) => {
    const { [id]: _drop, ...rest } = c;
    return rest;
  });

  const sendCart = async () => {
    setBusy(true);
    try {
      const items = cartItems.map(({ p, qty }) =>
        p.already_held && p.held_product_id
          ? { product_id: p.held_product_id, quantity: Number(qty) || null }
          : { supplier_product_id: p.id, quantity: Number(qty) || null });
      const { data } = await api.post("/restock-requests/batch", { items });
      toast.success(t("restock.cart_sent", { n: data.count }));
      setCart({});
      onRequested?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-4 border-t border-[#c9a24a]/40 pt-3">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between text-sm font-semibold text-[#8a6a1c]"
        data-testid="catalog-toggle"
      >
        {t("restock.catalog_browse")}
        {open ? <CaretUp size={15} weight="bold" /> : <CaretDown size={15} weight="bold" />}
      </button>
      {open && (
        <div className="mt-3 space-y-2" data-testid="supplier-catalog">
          <div className="relative">
            <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("restock.catalog_search")}
              className="input-brutal w-full !pl-9 h-10"
              data-testid="catalog-search"
            />
          </div>
          {catalog === null ? (
            <p className="text-sm text-zinc-500 py-2">{t("common.loading")}</p>
          ) : list.length === 0 ? (
            <p className="text-sm text-zinc-500 py-2">{t("restock.catalog_empty")}</p>
          ) : (
            <div className="max-h-96 overflow-y-auto space-y-1.5 pr-1">
              {list.map((p) => (
                <div
                  key={p.id}
                  className="rounded-lg border border-zinc-200 bg-white px-3 py-2"
                  data-testid={`catalog-item-${p.id}`}
                >
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                    <span
                      className={`w-2 h-2 rounded-full shrink-0 ${p.made_to_order ? "bg-amber-400" : p.available ? "bg-emerald-500" : "bg-zinc-300"}`}
                      title={p.made_to_order ? t("restock.catalog_made_to_order") : p.available ? t("restock.catalog_in_stock") : t("restock.catalog_out")}
                    />
                    <span className="font-medium text-sm">{p.name}</span>
                    {p.made_to_order && (
                      <span
                        className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-amber-50 border border-amber-300 text-amber-800"
                        data-testid={`made-to-order-badge-${p.id}`}
                      >
                        {t("restock.catalog_made_to_order")}
                      </span>
                    )}
                    {p.already_held && (
                      <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-emerald-50 border border-emerald-300 text-emerald-700">
                        {t("restock.catalog_you_carry")}
                      </span>
                    )}
                    <span className="ml-auto text-xs tabular-nums text-zinc-600" data-testid={`catalog-prices-${p.id}`}>
                      {p.ws != null && <><b>${p.ws.toFixed(2)}</b> {t("restock.catalog_ws")}</>}
                      {p.ws != null && p.srp != null && " · "}
                      {p.srp != null && <>${p.srp.toFixed(2)} {t("restock.catalog_srp")}</>}
                    </span>
                    {pendingIds.has(p.held_product_id) || pendingIds.has(p.id) ? (
                      <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#8a6a1c]">
                        <CheckCircle size={13} weight="fill" /> {t("restock.catalog_requested")}
                      </span>
                    ) : cart[p.id] ? (
                      <span className="inline-flex items-center gap-1.5" data-testid={`catalog-in-cart-${p.id}`}>
                        <BrutalInput
                          type="number"
                          min="1"
                          value={cart[p.id].qty}
                          onChange={(e) => setQtyFor(p, e.target.value)}
                          className="!h-8 w-20 !py-0 text-sm"
                          data-testid={`catalog-qty-${p.id}`}
                        />
                        <button
                          onClick={() => removeFromCart(p.id)}
                          className="text-zinc-400 hover:text-rose-600"
                          title={t("restock.cart_remove")}
                          data-testid={`catalog-remove-${p.id}`}
                        >
                          <X size={15} weight="bold" />
                        </button>
                      </span>
                    ) : (
                      <BrutalButton
                        size="sm"
                        variant="secondary"
                        onClick={() => setQtyFor(p, 10)}
                        data-testid={`catalog-add-${p.id}`}
                      >
                        <Plus size={13} weight="bold" /> {t("restock.cart_add")}
                      </BrutalButton>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          {cartItems.length > 0 && (
            <div
              className="sticky bottom-0 flex flex-wrap items-center gap-2 bg-[#fbf6e7] border border-[#c9a24a] rounded-lg px-3 py-2.5"
              data-testid="catalog-cart-bar"
            >
              <span className="text-sm font-semibold text-[#8a6a1c]">
                {t("restock.cart_count", { n: cartItems.length })}
              </span>
              <span className="text-xs text-zinc-500 truncate max-w-[50%]">
                {cartItems.map(({ p, qty }) => `${p.name} ×${qty || "?"}`).join(", ")}
              </span>
              <span className="ml-auto flex items-center gap-2">
                <button
                  onClick={() => setCart({})}
                  className="text-xs font-medium text-zinc-500 hover:text-zinc-800"
                  data-testid="catalog-cart-clear"
                >
                  {t("restock.cart_clear")}
                </button>
                <BrutalButton size="sm" disabled={busy} onClick={sendCart} data-testid="catalog-cart-send">
                  <PaperPlaneTilt size={14} weight="bold" /> {t("restock.cart_send", { n: cartItems.length })}
                </BrutalButton>
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
